import React, { useState, useEffect } from 'react';
import {
  X, AlertCircle, Check, Plus, Trash2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

const SimpleTeacherModal = ({ recommendation, onComplete, onClose }) => {
  const [formData, setFormData] = useState({
    recommender_name: recommendation?.recommender_name || '',
    recommender_email: recommendation?.recommender_email || '',
    recommender_type: recommendation?.recommender_type || 'teacher',
    classes: [], // Array of {subject: '', grade: ''}
    due_date: (() => {
      const date = new Date();
      date.setDate(date.getDate() + 45);
      return date.toISOString().split('T')[0];
    })()
  });

  const [newClass, setNewClass] = useState({ subject: '', grade: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Pre-populate if we have existing data
  useEffect(() => {
    if (recommendation) {
      setFormData(prev => ({
        ...prev,
        recommender_name: recommendation.recommender_name || prev.recommender_name,
        recommender_email: recommendation.recommender_email || '',
        recommender_type: recommendation.recommender_type || 'teacher',
        classes: recommendation.classes || [],
        due_date: recommendation.due_date ?
          new Date(recommendation.due_date).toISOString().split('T')[0] :
          prev.due_date
      }));

      // Parse subject into classes if it exists and classes is empty
      if (recommendation.subject && (!recommendation.classes || recommendation.classes.length === 0)) {
        const subjects = recommendation.subject.split(',').map(s => s.trim());
        const parsedClasses = subjects.map(subject => ({
          subject: subject,
          grade: '' // Will need to be filled in
        }));
        setFormData(prev => ({ ...prev, classes: parsedClasses }));
      }
    }
  }, [recommendation]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.recommender_name.trim()) {
      newErrors.recommender_name = 'Name is required';
    }

    if (formData.recommender_email && !isValidEmail(formData.recommender_email)) {
      newErrors.recommender_email = 'Please enter a valid email';
    }

    if (formData.classes.length === 0) {
      newErrors.classes = 'Please add at least one class';
    } else {
      formData.classes.forEach((cls, index) => {
        if (!cls.grade) {
          newErrors[`class_grade_${index}`] = 'Grade is required for each class';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addClass = () => {
    if (newClass.subject.trim() && newClass.grade) {
      setFormData(prev => ({
        ...prev,
        classes: [...prev.classes, { ...newClass }]
      }));
      setNewClass({ subject: '', grade: '' });
      setErrors(prev => ({ ...prev, classes: undefined }));
    }
  };

  const removeClass = (index) => {
    setFormData(prev => ({
      ...prev,
      classes: prev.classes.filter((_, i) => i !== index)
    }));
  };

  const updateClass = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      classes: prev.classes.map((cls, i) =>
        i === index ? { ...cls, [field]: value } : cls
      )
    }));
    // Clear specific error for this class grade
    if (field === 'grade' && value) {
      setErrors(prev => ({ ...prev, [`class_grade_${index}`]: undefined }));
    }
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleSave();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare the data
      const saveData = {
        recommender_name: formData.recommender_name,
        recommender_email: formData.recommender_email,
        recommender_type: formData.recommender_type,
        subject: formData.classes.map(c => c.subject).join(', '),
        classes: formData.classes, // Store the full class data
        due_date: new Date(formData.due_date),
        is_quick_entry: false, // No longer a quick entry after setup
        status: 'not_requested', // Update status to indicate ready for request
        updated_at: new Date().toISOString()
      };

      let result;

      // Check if this is a new recommendation (has temp ID) or existing one
      const isNewRecommendation = recommendation.id?.toString().startsWith('temp-');

      if (isNewRecommendation) {
        // Create new recommendation
        const { data, error } = await supabase
          .from('college_recommendation_letters')
          .insert([{
            ...saveData,
            user_id: recommendation.user_id,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Update existing recommendation
        const { data, error } = await supabase
          .from('college_recommendation_letters')
          .update(saveData)
          .eq('id', recommendation.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      toast.success('Teacher details saved!');

      // Pass the saved data to the parent
      onComplete(result);
    } catch (error) {
      console.error('Error saving teacher details:', error);
      toast.error('Failed to save details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-center justify-center p-5">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                {recommendation?.id?.toString().startsWith('temp-') ? 'Add New Teacher' : 'Edit Teacher Details'}
              </h2>
              <p className="text-sm text-stone-600 mt-1">
                Quick setup - just the essentials
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Content */}
          <div className="space-y-6 mb-8">
            {/* Name Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Teacher Name *
              </label>
              <input
                type="text"
                value={formData.recommender_name}
                onChange={(e) => setFormData(prev => ({ ...prev, recommender_name: e.target.value }))}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent ${
                  errors.recommender_name ? 'border-red-500' : 'border-stone-300'
                }`}
                placeholder="Ms. Johnson"
              />
              {errors.recommender_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.recommender_name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Email Address
                <span className="text-stone-500 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="email"
                value={formData.recommender_email}
                onChange={(e) => setFormData(prev => ({ ...prev, recommender_email: e.target.value }))}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent ${
                  errors.recommender_email ? 'border-red-500' : 'border-stone-300'
                }`}
                placeholder="teacher@school.edu"
              />
              {errors.recommender_email && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.recommender_email}
                </p>
              )}
            </div>

            {/* Type Field */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Type
              </label>
              <select
                value={formData.recommender_type}
                onChange={(e) => setFormData(prev => ({ ...prev, recommender_type: e.target.value }))}
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
              >
                <option value="teacher">Teacher</option>
                <option value="counselor">Counselor</option>
                <option value="coach">Coach/Activity Advisor</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Classes Section */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-4">
                Classes Taken *
              </label>

              {/* Existing Classes */}
              <div className="space-y-3 mb-4">
                {formData.classes.map((cls, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={cls.subject}
                        onChange={(e) => updateClass(index, 'subject', e.target.value)}
                        placeholder="Subject (e.g., AP Biology)"
                        className="w-full px-3 py-2 text-sm border border-stone-300 rounded focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                      />
                    </div>
                    <div className="w-24">
                      <select
                        value={cls.grade}
                        onChange={(e) => updateClass(index, 'grade', e.target.value)}
                        className={`w-full px-3 py-2 text-sm border rounded focus:ring-2 focus:ring-azure-500 focus:border-transparent ${
                          errors[`class_grade_${index}`] ? 'border-red-500' : 'border-stone-300'
                        }`}
                      >
                        <option value="">Grade</option>
                        <option value="9">9th</option>
                        <option value="10">10th</option>
                        <option value="11">11th</option>
                        <option value="12">12th</option>
                      </select>
                    </div>
                    <button
                      onClick={() => removeClass(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Add New Class */}
              <div className="flex items-center gap-3 p-3 border-2 border-dashed border-stone-300 rounded-lg">
                <div className="flex-1">
                  <input
                    type="text"
                    value={newClass.subject}
                    onChange={(e) => setNewClass(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Subject (e.g., AP Biology)"
                    className="w-full px-3 py-2 text-sm border border-stone-300 rounded focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                  />
                </div>
                <div className="w-24">
                  <select
                    value={newClass.grade}
                    onChange={(e) => setNewClass(prev => ({ ...prev, grade: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-stone-300 rounded focus:ring-2 focus:ring-azure-500 focus:border-transparent"
                  >
                    <option value="">Grade</option>
                    <option value="9">9th</option>
                    <option value="10">10th</option>
                    <option value="11">11th</option>
                    <option value="12">12th</option>
                  </select>
                </div>
                <button
                  onClick={addClass}
                  disabled={!newClass.subject.trim() || !newClass.grade}
                  className="px-3 sm:px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-stone-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>

              {errors.classes && (
                <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.classes}
                </p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Request Deadline
              </label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-stone-500">
                When do you need the recommendation letter by?
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-stone-200">
            <button
              onClick={onClose}
              className="px-6 py-2.5 text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2.5 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:bg-azure-400 transition-colors flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Teacher
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleTeacherModal;